import type { ReactNode } from 'react';
import type { Block } from '@/content/blocks';

/**
 * 行内标记只支持两种：`**加粗**` 与 `` `等宽` ``。
 * 内容层是结构化数据而不是 markdown，所以不需要更完整的解析器 —— 需要链接的地方
 * 直接写成 `<a>` 反而更清楚。
 */
const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function inline(text: string): ReactNode[] {
  return text
    .split(INLINE_PATTERN)
    .filter((chunk) => chunk !== '')
    .map((chunk, index) => {
      if (chunk.startsWith('**') && chunk.endsWith('**')) {
        return <strong key={index}>{chunk.slice(2, -2)}</strong>;
      }
      if (chunk.startsWith('`') && chunk.endsWith('`')) {
        return <code key={index}>{chunk.slice(1, -1)}</code>;
      }
      return <span key={index}>{chunk}</span>;
    });
}

export function renderBlock(block: Block, index: number): ReactNode {
  switch (block.kind) {
    case 'p':
      return <p key={index}>{inline(block.text)}</p>;
    case 'h2':
      return (
        <h2 key={index} id={anchorId(block.text)}>
          {block.text}
        </h2>
      );
    case 'h3':
      return <h3 key={index}>{block.text}</h3>;
    case 'ul':
      return (
        <ul key={index}>
          {block.items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={index}>
          {block.items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ol>
      );
    case 'quote':
      return <blockquote key={index}>{inline(block.text)}</blockquote>;
    case 'code':
      return (
        <pre key={index}>
          <code>{block.code}</code>
        </pre>
      );
    case 'table':
      return (
        <div key={index} className="-mx-5 overflow-x-auto px-5 md:mx-0 md:px-0">
          <table>
            <thead>
              <tr>
                {block.head.map((cell) => (
                  <th key={cell} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

/** h2 文本转成锚点 id，用于文内跳转；中文没法拼音化，直接保留字符是可靠的。 */
export function anchorId(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u4e00-\u9fa5-]/g, '');
}

export function Prose({ blocks, className }: { blocks: readonly Block[]; className?: string }) {
  return <div className={['prose-article', className].filter(Boolean).join(' ')}>{blocks.map(renderBlock)}</div>;
}
